import { COLLIDERS, terrainHeight, type Collider } from "./world.ts";
import { SHIP_COLLIDERS } from "./ship.ts";
import { worldToLocal, type Pose, type Vec3 } from "./math.ts";

/** First solid entry on the open finite segment, including an origin inside it. */
export function segmentColliderEntry(a: Vec3, b: Vec3, c: Collider): number {
  let lo = 0, hi = 1;
  const dy = b.y - a.y;
  if (dy === 0) {
    if (a.y < c.y0 || a.y > c.y1) return Infinity;
  } else {
    const t0 = (c.y0 - a.y) / dy, t1 = (c.y1 - a.y) / dy;
    lo = Math.max(lo, Math.min(t0, t1));
    hi = Math.min(hi, Math.max(t0, t1));
  }
  const dx = b.x - a.x, dz = b.z - a.z;
  if (c.kind === "box") {
    if (dx === 0) {
      if (a.x < c.minX || a.x > c.maxX) return Infinity;
    } else {
      const t0 = (c.minX - a.x) / dx, t1 = (c.maxX - a.x) / dx;
      lo = Math.max(lo, Math.min(t0, t1)); hi = Math.min(hi, Math.max(t0, t1));
    }
    if (dz === 0) {
      if (a.z < c.minZ || a.z > c.maxZ) return Infinity;
    } else {
      const t0 = (c.minZ - a.z) / dz, t1 = (c.maxZ - a.z) / dz;
      lo = Math.max(lo, Math.min(t0, t1)); hi = Math.min(hi, Math.max(t0, t1));
    }
  } else {
    const x = a.x - c.x, z = a.z - c.z;
    const aa = dx * dx + dz * dz, bb = 2 * (x * dx + z * dz), cc = x * x + z * z - c.r * c.r;
    if (aa === 0) {
      if (cc > 0) return Infinity;
    } else {
      const discriminant = bb * bb - 4 * aa * cc;
      if (discriminant < 0) return Infinity;
      const root = Math.sqrt(discriminant);
      lo = Math.max(lo, (-bb - root) / (2 * aa)); hi = Math.min(hi, (-bb + root) / (2 * aa));
    }
  }
  return lo <= hi && hi > 0 && lo < 1 ? lo : Infinity;
}

/** Whether a solid intersects the open segment. */
export function segmentIntersectsCollider(a: Vec3, b: Vec3, c: Collider): boolean {
  return segmentColliderEntry(a, b, c) !== Infinity;
}

const localA: Vec3 = { x: 0, y: 0, z: 0 }, localB: Vec3 = { x: 0, y: 0, z: 0 };
/** Visibility against world solids, optional hulls/furniture, and terrain. */
export function worldVisible(a: Vec3, b: Vec3, ships?: readonly (Pose | null | undefined)[]): boolean {
  for (const collider of COLLIDERS) if (segmentIntersectsCollider(a, b, collider)) return false;
  if (ships) for (const ship of ships) {
    if (!ship) continue;
    worldToLocal(ship, a.x, a.y, a.z, localA);
    worldToLocal(ship, b.x, b.y, b.z, localB);
    for (const collider of SHIP_COLLIDERS) if (segmentIntersectsCollider(localA, localB, collider)) return false;
  }
  const samples = Math.max(1, Math.ceil(Math.hypot(b.x - a.x, b.y - a.y, b.z - a.z) / 0.1));
  for (let i = 0; i < samples; i++) {
    const t = (i + 0.5) / samples, x = a.x + (b.x - a.x) * t, y = a.y + (b.y - a.y) * t, z = a.z + (b.z - a.z) * t;
    if (y < terrainHeight(x, z)) return false;
  }
  return true;
}
